import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type {
	PublicBookingInfo,
	PublicBookingAvailability,
	CreatePublicBookingRequest,
	CreatePublicBookingResponse,
} from '../types/publicBooking';

// Every call here is made by a customer who is not signed in, so `skipAuth`
// keeps any portal session that happens to live in this browser out of the
// request.
const PUBLIC = { skipAuth: true } as const;

export const publicBookingApi = {
	/**
	 * Provider, location, services and (for paid links) staff for a booking token.
	 * First call on both booking pages — `paymentRequired` decides which renders.
	 */
	getInfo: async (token: string): Promise<PublicBookingInfo> =>
		apiClient.get<PublicBookingInfo>(API_ENDPOINTS.PUBLIC_BOOKING.INFO(token), PUBLIC),

	/**
	 * Slots for a single date, with existing appointments already accounted for.
	 * @param date `yyyy-MM-dd`
	 * @param options.staffId scopes availability to one stylist (paid links)
	 * @param options.durationMinutes how long the whole basket takes, services
	 * and add-ons together. Without it the server can only tell whether a slot
	 * starts free, so a job too long to finish that day is still offered.
	 */
	getAvailability: async (
		token: string,
		date: string,
		options: { staffId?: number; durationMinutes?: number } = {}
	): Promise<PublicBookingAvailability> => {
		const params = new URLSearchParams({ date });
		if (options.staffId != null) params.append('staffId', String(options.staffId));
		// A zero or missing duration is nothing to fit a job into, so leave the
		// param off entirely and let the server fall back to its old behaviour.
		if (options.durationMinutes) {
			params.append('durationMinutes', String(options.durationMinutes));
		}
		return apiClient.get<PublicBookingAvailability>(
			`${API_ENDPOINTS.PUBLIC_BOOKING.AVAILABILITY(token)}?${params.toString()}`,
			PUBLIC
		);
	},

	/** Create the appointment behind a booking token. */
	create: async (
		token: string,
		data: CreatePublicBookingRequest
	): Promise<CreatePublicBookingResponse> =>
		apiClient.post<CreatePublicBookingResponse>(
			API_ENDPOINTS.PUBLIC_BOOKING.CREATE(token),
			data,
			PUBLIC
		),
};
